'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faGlobe, faChartLine } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import useAnalytics from 'src/hooks/analytics/useAnalytics';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { useAvailableDataRange } from 'src/hooks/analytics';
import { TableRowActions } from '../';
import { AnalyticsErrorOverlay } from '../AnalyticsErrorOverlay';
import { AnalyticsTableHeader } from '../AnalyticsTableHeader';
import {
  Tooltip,
  PulseHeader,
  PulseCell,
  PulseColumnMenu,
  PulseColumnDef,
  PulseLoading,
} from 'src/components';
import {
  useGetTrendingAnalyticsQuery,
  useCreateBulkExportV2Mutation,
} from 'src/api/redux';
import { ANALYTICS_LOCATION } from 'src/hooks/filters/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { expandPreset, formatPeriodLabel, getPeriodKey, isPeriodIncomplete, toApiPeriod } from 'src/utils/quarterUtils';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useAuth } from 'src/context/auth';
import { SortSection } from './SortSection';
import { getNewExport } from 'src/api/exports/exportsApi';
import { AnalyticsResultItem } from '../types';
import { ANALYTICS_DEFAULT_DATE_RANGE } from 'src/components/constants';

// Literal grid col-span classes for the year-group header (a year spans up to
// its 4 quarters). Kept as literals so Tailwind's JIT generates them.
const COL_SPAN_CLASS: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
};

// Type for the trends data returned from API
type TrendsResultItem = AnalyticsResultItem<{
  claims_trends?: Record<string, number>;
  claims_total?: number;
  patients_trends?: Record<string, number>;
  patients_total?: number;
}>;

const AnalyticsTrendsTable = () => {
  const { user } = useAuth();
  const auth = useAuthorization();
  const isMedscout = user?.is_superuser || user?.permissions?.is_support_user;
  const userCanBulkExport =
    isMedscout || auth.hasPermission('export.providers_in_territory.all');

  const log = useLogging();
  const { setNotification } = useNotification();
  // Redux state and actions (TRENDS location)
  const {
    selectedGeographyObjects = [],
    selectedCodeSet = [],
    formattedFiltersForAPI,
    date_range = ANALYTICS_DEFAULT_DATE_RANGE,
    granularity = 'quarter',
    periods = [],
    timePreset = 'last4',
    volumeTypes = [],
    modeType,
    volumeConfigs = {},
  } = useAnalytics(ANALYTICS_LOCATION.TRENDS) || {};

  const availableRange = useAvailableDataRange();

  // Under the `quarterly-ta-data` flag, send the granularity + periods contract.
  // `granularity` lives INSIDE `date_range` (it's a field of DateRangeSerializer)
  // — a top-level granularity is ignored by the backend and silently collapses
  // results to year buckets. Otherwise keep the legacy gte/lte year range.
  const { quarterlyTaData } = useFlags();
  const timeFilter = useMemo(() => {
    if (quarterlyTaData) {
      return {
        date_range: {
          granularity,
          periods: periods.map(toApiPeriod),
        },
      };
    }
    return {
      date_range: {
        gte: parseInt(date_range.gte),
        lte: parseInt(date_range.lte),
      },
    };
  }, [quarterlyTaData, granularity, periods, date_range]);

  // Bulk export mutation
  const [createBulkExportV2] = useCreateBulkExportV2Mutation();

  // The data columns are driven off the time selection. Under the quarterly
  // flag, columns come from the exact `periods` the user sent (request order,
  // including non-contiguous selections) keyed by period key ("2024-Q1" /
  // "2024"). Otherwise we fall back to the legacy contiguous year range.
  const periodColumns = useMemo(() => {
    if (quarterlyTaData) {
      // While the available range is loading, render columns for any committed
      // periods without incompleteness marking; presets need the range.
      if (!availableRange) {
        return periods.map((period) => ({
          field: getPeriodKey(period),
          headerName: formatPeriodLabel(period),
          isIncomplete: false,
        }));
      }
      
      const activePeriods = periods.length
        ? periods
        : expandPreset(
            timePreset === 'custom' ? 'last4' : timePreset,
            granularity,
            availableRange
          );
      return activePeriods.map((period) => ({
        field: getPeriodKey(period),
        headerName: formatPeriodLabel(period),
        isIncomplete: isPeriodIncomplete(period, availableRange.latestComplete),
      }));
    }

    const startYear = parseInt(date_range.gte);
    const endYear = parseInt(date_range.lte);
    return Array.from({ length: endYear - startYear + 1 }, (_unused, index) => {
      const year = startYear + index;
      return {
        field: String(year),
        headerName: String(year),
        isIncomplete: availableRange
          ? isPeriodIncomplete({ year }, availableRange.latestComplete)
          : false,
      };
    });
  }, [
    quarterlyTaData,
    periods,
    timePreset,
    granularity,
    availableRange,
    date_range,
  ]);

  // Field → "incomplete period" flag, for the data-lag tooltip in headers.
  const incompleteByField = useMemo(() => {
    const map = new Map<string, boolean>();
    periodColumns.forEach(({ field, isIncomplete }) =>
      map.set(field, isIncomplete)
    );
    return map;
  }, [periodColumns]);

  // In quarter granularity the header is grouped into two rows: a year row that
  // spans its quarters, with the quarter cells beneath. Periods are
  // chronological, so each year's quarters are always adjacent columns.
  const isGroupedHeader = quarterlyTaData && granularity === 'quarter';
  const yearGroups = useMemo(() => {
    if (!isGroupedHeader) return [];
    const groups: { year: string; span: number }[] = [];
    periodColumns.forEach(({ field }) => {
      const year = field.split('-')[0];
      const last = groups[groups.length - 1];
      if (last && last.year === year) last.span += 1;
      else groups.push({ year, span: 1 });
    });
    return groups;
  }, [isGroupedHeader, periodColumns]);

  // Define columns using PulseColumnDef. The trailing Total column shows in both
  // the legacy and quarterly views.
  const columns: PulseColumnDef[] = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'Geography',
        width: 272,
        resizable: false,
        sortable: true,
      },
      ...periodColumns.map(({ field, headerName }) => ({
        field,
        headerName,
        width: 150,
        minWidth: 150,
        maxWidth: 300,
        resizable: true,
        sortable: true,
      })),
      {
        field: 'total',
        headerName: 'Total',
        width: 150,
        minWidth: 150,
        maxWidth: 300,
        resizable: true,
        sortable: true,
      },
    ];
  }, [periodColumns]);

  // The period/year data columns (everything between Geography and Total).
  const dataColumns = useMemo(() => columns.slice(1, -1), [columns]);

  // Use column resizing hook
  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns });

  const [sort, setSort] = useState<{
    sort: string;
    order: 'asc' | 'desc';
  } | null>({
    sort: 'name',
    order: 'asc',
  });

  const totalColumnProps = useMemo(
    () => ({
      totalCol: columns[columns.length - 1],
      sortValue: `${volumeTypes?.[0] || 'claims'}_total`,
    }),
    [columns, volumeTypes]
  );

  useEffect(() => {
    if (sort && sort.sort !== 'name') {
      const currentVolumeType = volumeTypes?.[0] || 'claims';
      if (!sort.sort.includes(currentVolumeType)) {
        setSort({
          sort: 'name',
          order: 'asc',
        });
      }
    }
  }, [volumeTypes, sort]);

  const shouldFetch = useMemo(() => {
    if (!selectedCodeSet) return false;
    return (
      Array.isArray(selectedGeographyObjects) &&
      selectedGeographyObjects.length > 0 &&
      selectedCodeSet?.length > 0
    );
  }, [selectedGeographyObjects, selectedCodeSet]);

  // Build volume_configs for API - Trends table has only one code set
  const apiVolumeConfigs = useMemo(() => {
    if (!selectedCodeSet?.[0]) return {};

    const codeSetId = selectedCodeSet[0].id.toString();

    // Use existing volumeConfig if available, otherwise create from current state
    const config = volumeConfigs[codeSetId] || {
      code_set: selectedCodeSet[0].id,
      mode: modeType || 'treating',
    };

    return {
      [codeSetId]: config,
    };
  }, [selectedCodeSet, volumeConfigs, volumeTypes, date_range, modeType]);

  const {
    currentData: trendsData,
    isLoading: isLoadingTrends,
    isFetching: isFetchingTrends,
    isError: isErrorTrends,
    error: errorTrends,
  } = useGetTrendingAnalyticsQuery(
    {
      params: {
        sort: sort?.sort,
        order: sort?.order,
      },
      data: {
        volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
        ...timeFilter,
        volume_configs: apiVolumeConfigs,
        geographies: selectedGeographyObjects.map((g) => ({
          type: g.type,
          id: g.id,
        })),
        ...(formattedFiltersForAPI.length > 0 && {
          filters: formattedFiltersForAPI,
        }),
      },
    },
    {
      skip: !shouldFetch,
    }
  );

  // Handle bulk export with provider or site type
  const handleBulkExport = async (type: 'provider' | 'site', id: string) => {
    log.event('Export initiated', {
      source: 'AnalyticsTrendsTable',
      exportType: type,
    });

    const geographyToSend = selectedGeographyObjects.find(
      (geo) => geo.id.toString() === id
    );
    if (!geographyToSend) {
      setNotification({
        title: 'Error',
        message: 'Selected geography not found for export.',
        type: 'error',
      });
      return;
    }

    try {
      await createBulkExportV2({
        data: {
          volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
          ...timeFilter,
          volume_configs: apiVolumeConfigs,
          geographies: [
            {
              type: geographyToSend.type,
              id: geographyToSend.id,
            },
          ],
          ...(formattedFiltersForAPI.length > 0 && {
            filters: formattedFiltersForAPI,
          }),
        },
        type,
      }).unwrap();
      setNotification({
        title: 'Success',
        message:
          'Export requested, you will receive an email when it is ready!',
        type: 'success',
      });
    } catch (err) {
      log.exception(`Error exporting territory analytics (${type}): ${err}`);
      setNotification({
        title: 'Error',
        message: err?.data?.error_message || 'Error requesting export',
        type: 'error',
      });
    }
  };

  const { closeAllButCurrent } = useDrawer();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const displayedGeographies = useMemo((): TrendsResultItem[] => {
    const geographies = Array.isArray(selectedGeographyObjects)
      ? selectedGeographyObjects
      : [];

    if (trendsData?.results && trendsData.results.length > 0) {
      // Rows are identified by (type, id) — ids can collide across territory and
      // region, so the type must be part of the key.
      const selectedIds = new Set<string>(
        geographies.map((g) => `${g.type}-${g.id}`)
      );

      const sortedResults = trendsData.results.filter(
        (result: TrendsResultItem) =>
          selectedIds.has(`${result.type}-${result.id}`)
      );

      const resultsMap = new Map<string, TrendsResultItem>(
        trendsData.results.map((result: TrendsResultItem) => [
          `${result.type}-${result.id}`,
          result,
        ])
      );

      const missingGeographies = geographies
        .filter(
          (geography) => !resultsMap.has(`${geography.type}-${geography.id}`)
        )
        .map(
          (geography): TrendsResultItem => ({
            id: geography.id,
            name: geography.name || '',
            type: geography.type,
          })
        );

      return [...sortedResults, ...missingGeographies];
    }

    return geographies.map(
      (geography): TrendsResultItem => ({
        id: geography.id,
        name: geography.name || '',
        type: geography.type,
      })
    );
  }, [trendsData, selectedGeographyObjects]);

  // Handle table export
  const handleExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      try {
        // Build query params for the export API
        const queryParams = new URLSearchParams({
          sort: sort?.sort || 'name',
          order: sort?.order || 'asc',
        });

        await getNewExport({
          url: `https://api.medscout.io/api/v1/analytics/territory-insights/trends/?${queryParams.toString()}`,
          method: 'POST',
          fileFormat: format,
          columns: [
            'id',
            'name',
            'type',
            `${volumeTypes?.[0]}_trends`,
            `${volumeTypes?.[0]}_total`,
          ],
          fileName: `analytics-trends-${
            new Date().toISOString().split('T')[0]
          }`,
          data: {
            volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
            ...timeFilter,
            volume_configs: apiVolumeConfigs,
            geographies: selectedGeographyObjects.map((g) => ({
              type: g.type,
              id: g.id,
            })),
            ...(formattedFiltersForAPI.length > 0 && {
              filters: formattedFiltersForAPI,
            }),
          },
        });

        setNotification({
          title: 'Success',
          message:
            'Export initiated - You will receive an email when it is ready for download',
          type: 'success',
        });

        log.event('Export', {
          type: ANALYTICS_LOCATION.TRENDS,
          source: 'Analytics Trends Table',
          columns: columns.map((col) => col.field),
          count: displayedGeographies.length,
          format,
        });
      } catch (err) {
        log.exception(`Error exporting trends: ${err}`);

        setNotification({
          title: 'Error',
          message: err?.error_message || 'Error exporting, please try again.',
          type: 'error',
        });
      }
    },
    [
      columns,
      volumeTypes,
      timeFilter,
      apiVolumeConfigs,
      modeType,
      selectedGeographyObjects,
      formattedFiltersForAPI,
      sort,
      log,
      setNotification,
      displayedGeographies,
    ]
  );

  const handleOpenTerritoryDrawer = useCallback(() => {
    log.event('Territory drawer opened', {
      source: 'AnalyticsTrendsTable',
    });
    closeAllButCurrent('analytics-territory-drawer');
  }, [closeAllButCurrent, log]);

  const handleEllipsisClick = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.stopPropagation();
      const isOpening = openMenuId !== columnId;
      if (isOpening) {
        log.event('Column menu opened', {
          source: 'AnalyticsTrendsTable',
          columnId,
        });
      }
      setOpenMenuId((prev) => (prev === columnId ? null : columnId));
    },
    [openMenuId, log]
  );

  const handleSortToggle = useCallback(
    (columnName: string, order?: 'asc' | 'desc') => {
      setSort((prevSort) => {
        const newOrder =
          order ||
          (prevSort?.sort === columnName
            ? prevSort.order === 'asc'
              ? 'desc'
              : 'asc'
            : 'desc');
        log.event('Sort changed', {
          source: 'AnalyticsTrendsTable',
          columnName,
          sortOrder: newOrder,
        });
        if (order) {
          return {
            sort: columnName,
            order,
          };
        }
        if (prevSort?.sort === columnName) {
          return {
            sort: columnName,
            order: prevSort.order === 'asc' ? 'desc' : 'asc',
          };
        }
        return {
          sort: columnName,
          order: 'desc',
        };
      });
      setOpenMenuId(null);
    },
    [log]
  );

  const hasGeographies = useMemo(() => {
    return (
      Array.isArray(selectedGeographyObjects) &&
      selectedGeographyObjects.length > 0
    );
  }, [selectedGeographyObjects]);

  const _hasRegions = useMemo(() => {
    return selectedGeographyObjects.some((geo) => geo.type === 'region');
  }, [selectedGeographyObjects]);

  const hasCodeSets = useMemo(() => {
    return selectedCodeSet?.length > 0;
  }, [selectedCodeSet]);

  const showGeographyEmptyState = !hasGeographies;
  const showCodeSetEmptyState = !hasCodeSets;
  const showLoadingOverlay =
    (isLoadingTrends || isFetchingTrends) && hasGeographies && hasCodeSets;

  // A single period/year data-column header. In grouped mode it's the quarter
  // cell (row 2, labelled "Q1"); otherwise the year/period cell (single row).
  const renderDataColumnHeader = (col: PulseColumnDef) => {
    const sortValue = `${volumeTypes?.[0] || 'claims'}_trends.${col.field}`;
    const isMenuOpen = openMenuId === sortValue;
    const isActiveSortColumn = sort?.sort === sortValue;
    const columnLabel = isGroupedHeader
      ? col.field.split('-')[1] ?? col.headerName
      : col.headerName;
    // Under the flag, drive the data-lag tooltip off the period; with the flag
    // off keep the legacy hardcoded 2025/2026 behavior unchanged.
    const showLagTooltip = quarterlyTaData
      ? incompleteByField.get(col.field) ?? false
      : col.field === '2025' || col.field === '2026';
    const lagMessage =
      !quarterlyTaData && col.field === '2026'
        ? 'Current calendar year trends are still populating. Data lag from commercial clearinghouses and CMS means recent months may be incomplete.'
        : quarterlyTaData
        ? 'Trends for the current period are still populating. Data lag from commercial clearinghouses and CMS means recent months may be incomplete.'
        : 'Data lag from commercial clearinghouses and CMS means recent months may be incomplete.';

    return (
      <PulseHeader
        key={col.field}
        label={
          showLagTooltip ? (
            <div className="flex items-center gap-1.5">
              <span>{columnLabel}</span>
              <Tooltip
                content={lagMessage}
                slotProps={{
                  containerClassName: '!bg-neutral-900 !text-white',
                }}
                className="cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <Icon icon={faInfoCircle} className="w-4 h-4 text-secondary" />
                </div>
              </Tooltip>
            </div>
          ) : (
            columnLabel
          )
        }
        isSticky
        stickyPosition="top"
        className={`sticky z-20 bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200 ${
          isGroupedHeader ? 'top-9' : 'top-0'
        }`}
        sortable={col.sortable}
        sortDirection={isActiveSortColumn ? sort.order : null}
        resizable={col.resizable}
        onStartResize={
          col.resizable ? (e) => startResizing(col.field, e) : undefined
        }
        isResizing={isResizing}
        menu={
          <PulseColumnMenu
            columnId={sortValue}
            isOpen={isMenuOpen}
            onToggle={(e) => handleEllipsisClick(e, sortValue)}
            customSectionComponents={[
              <SortSection
                key="sort"
                currentOrder={sort?.order || null}
                isActive={isActiveSortColumn}
                onChange={(order) => handleSortToggle(sortValue, order)}
              />,
            ]}
          />
        }
      />
    );
  };

  // The Total header. In grouped mode it spans both header rows.
  const renderTotalHeader = (grouped: boolean) => (
    <PulseHeader
      label={totalColumnProps.totalCol.headerName}
      isSticky
      stickyPosition="top"
      className={`sticky top-0 z-20 bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200 ${
        grouped ? 'row-span-2' : ''
      }`}
      sortable={totalColumnProps.totalCol.sortable}
      sortDirection={
        sort?.sort === totalColumnProps.sortValue ? sort.order : null
      }
      resizable={totalColumnProps.totalCol.resizable}
      onStartResize={
        totalColumnProps.totalCol.resizable
          ? (e) => startResizing(totalColumnProps.totalCol.field, e)
          : undefined
      }
      isResizing={isResizing}
      menu={
        <PulseColumnMenu
          columnId={totalColumnProps.sortValue}
          isOpen={openMenuId === totalColumnProps.sortValue}
          onToggle={(e) => handleEllipsisClick(e, totalColumnProps.sortValue)}
          customSectionComponents={[
            <SortSection
              key="sort"
              currentOrder={sort?.order || null}
              isActive={sort?.sort === totalColumnProps.sortValue}
              onChange={(order) =>
                handleSortToggle(totalColumnProps.sortValue, order)
              }
            />,
          ]}
        />
      }
    />
  );

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden bg-white relative rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.00)] border border-neutral-200"
      data-testid="analytics-trends-table"
    >
      <AnalyticsTableHeader
        columns={dataColumns.map((col) => col.field)}
        location={ANALYTICS_LOCATION.TRENDS}
        onExport={handleExport}
      />

      <div
        className={`flex-1 relative ${
          hasGeographies ? 'overflow-x-auto overflow-y-auto' : 'overflow-hidden'
        }`}
      >
        {/* Full-height separator line - positioned to stay with sticky Geography
            column. Offset below the header (taller when the grouped two-row
            quarter header is shown). */}
        {(hasGeographies || showGeographyEmptyState) && (
          <div
            className={`sticky left-[271px] w-px bg-neutral-200 z-[21] pointer-events-none float-left -ml-px ${
              isGroupedHeader
                ? 'top-[84px] h-[calc(100%-84px)]'
                : 'top-12 h-[calc(100%-3rem)]'
            }`}
          />
        )}

        <div
          role="table"
          aria-label="Analytics trends data by geography"
          className="grid relative w-full"
          style={{
            gridTemplateColumns: getGridTemplateColumns(),
            gridAutoFlow: 'row',
          }}
        >
          {/* Geography Header (spans both rows when the header is grouped) */}
          <PulseHeader
            label="Geography"
            isSticky
            stickyPosition="both"
            className={`sticky left-0 top-0 z-30 bg-neutral-50 text-neutral-900 border-b border-r border-b-neutral-200 border-r-neutral-200 ${
              isGroupedHeader ? 'row-span-2' : ''
            }`}
            sortable
            sortDirection={sort?.sort === 'name' ? sort.order : null}
            resizable={false}
            menu={
              <PulseColumnMenu
                columnId="name"
                isOpen={openMenuId === 'name'}
                onToggle={(e) => handleEllipsisClick(e, 'name')}
                customSectionComponents={[
                  <SortSection
                    key="sort"
                    currentOrder={sort?.order || null}
                    isActive={sort?.sort === 'name'}
                    onChange={(order) => handleSortToggle('name', order)}
                  />,
                ]}
              />
            }
          />

          {isGroupedHeader ? (
            <>
              {/* Year-group row (row 1). Year cells, then the row-spanning Total
                  and filler, are rendered before the quarter cells so the grid
                  auto-flow fills row 1 first, then the quarter cells drop to
                  row 2. */}
              {yearGroups.map((group) => (
                <div
                  key={`year-${group.year}`}
                  role="columnheader"
                  className={`${
                    COL_SPAN_CLASS[group.span] ?? 'col-span-1'
                  } sticky top-0 z-20 h-9 flex items-center justify-center bg-neutral-50 text-neutral-800 text-xs font-semibold font-['Inter'] border-b border-r border-b-neutral-200 border-r-neutral-200`}
                >
                  {group.year}
                </div>
              ))}
              {/* Total spans both header rows */}
              {renderTotalHeader(true)}
              {/* Filler spans both header rows */}
              <div className="row-span-2 sticky top-0 z-20 bg-neutral-50 border-b border-b-neutral-200" />
              {/* Quarter row (row 2) */}
              {dataColumns.map(renderDataColumnHeader)}
            </>
          ) : (
            <>
              {/* Single-row data headers, Total, then filler */}
              {dataColumns.map(renderDataColumnHeader)}
              {renderTotalHeader(false)}
              <div className="bg-neutral-50 border-b border-b-neutral-200" />
            </>
          )}

          {/* Geography Empty State */}
          {showGeographyEmptyState && (
            <>
              <div className="flex sticky left-0 items-center justify-center w-[272px] h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Icon icon={faGlobe} className="text-2xl" />
                  <span>Select Geographies</span>
                  <button
                    type="button"
                    onClick={handleOpenTerritoryDrawer}
                    className="px-4 py-2 bg-white text-secondary border border-neutral-200 text-sm font-medium font-['Inter'] rounded-full hover:bg-neutral-100 transition-all duration-300"
                  >
                    Add Geographies
                  </button>
                </div>
              </div>
              {/* Filler column for empty state */}
              <div className="bg-white" />
            </>
          )}

          {/* Code Set Empty State - when no geographies */}
          {showGeographyEmptyState && showCodeSetEmptyState && (
            <div
              className="flex items-center justify-center bg-white h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
              style={{ gridColumn: '2 / -1' }}
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <Icon icon={faChartLine} className="text-2xl" />
                <span>Select Code Sets to see trends data</span>
              </div>
            </div>
          )}

          {/* Geography Rows and Data Cells */}
          {displayedGeographies.map((geography) => {
            const isRegion = geography.type === 'region';

            return (
              <React.Fragment key={`${geography.type}-${geography.id}`}>
                <PulseCell
                  isSticky
                  stickyPosition="left"
                  className="sticky left-0 z-20 border-b border-b-neutral-200 bg-white group"
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        data-testid={`geography-name-${geography.id}`}
                        className="truncate text-secondary text-sm font-normal font-['Inter'] leading-5"
                        title={geography.name}
                      >
                        {geography.name}
                      </span>
                      {isRegion && (
                        <Tooltip
                          content="This is a region"
                          slotProps={{
                            containerClassName: '!bg-neutral-900 !text-white',
                          }}
                        >
                          <span
                            className="inline-flex items-center text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full cursor-default shrink-0"
                            data-testid={`region-badge-${geography.id}`}
                            role="status"
                            aria-label="Region indicator"
                          >
                            Region
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <TableRowActions
                      geographyId={geography.id}
                      onBulkExport={
                        userCanBulkExport ? handleBulkExport : undefined
                      }
                      location={ANALYTICS_LOCATION.TRENDS}
                      isRegion={isRegion}
                    />
                  </div>
                </PulseCell>

                {/* Period columns */}
                {dataColumns.map((col) => {
                  const trendsData = geography?.[
                    `${volumeTypes?.[0]}_trends`
                  ] as Record<string, number> | null | undefined;
                  const value = trendsData?.[col.field];
                  return (
                    <PulseCell
                      key={col.field}
                      className={`bg-white h-12 ${hasCodeSets ? 'border-b border-r border-b-neutral-200 border-r-neutral-200' : ''}`}
                    >
                      {hasCodeSets &&
                        (value != null ? handleNumericValue(value) : 'n/a')}
                    </PulseCell>
                  );
                })}

                {/* Total column */}
                <PulseCell
                  className={`bg-white h-12 ${hasCodeSets ? 'border-b border-r border-b-neutral-200 border-r-neutral-200' : ''}`}
                >
                  {hasCodeSets &&
                    (() => {
                      const totalValue = geography?.[
                        `${volumeTypes?.[0]}_total`
                      ] as number | null | undefined;
                      return totalValue != null
                        ? handleNumericValue(totalValue)
                        : 'n/a';
                    })()}
                </PulseCell>

                {/* Filler Column Cell - empty cell to fill remaining space */}
                <div className={`bg-white ${hasCodeSets ? 'border-b border-b-neutral-200' : ''}`} />
              </React.Fragment>
            );
          })}
        </div>

        {/* Code Set Empty State Overlay - when we have geographies but no code sets */}
        {!showGeographyEmptyState && showCodeSetEmptyState && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="code-set-empty-state-overlay"
          >
            <div className="flex flex-col items-center justify-center gap-2">
              <Icon icon={faChartLine} className="text-2xl" />
              <span>Select Code Sets to see trends data</span>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        <AnalyticsErrorOverlay
          error={errorTrends}
          isVisible={isErrorTrends && !isLoadingTrends && !isFetchingTrends}
        />

        {showLoadingOverlay && <PulseLoading />}
      </div>
    </div>
  );
};

export default AnalyticsTrendsTable;
